import {applyFilters, applyOffsetAndLimit, applySort} from '@utils/api-utils'
import getUrl from '@utils/url-utils'

import {baseApi} from './base.slice'

const PRIZES_ENDPOINT = 'prizes'
const PRIZE_CATEGORIES_ENDPOINT = 'prizes/categories'
const BASE_PRIZE_URL = new URL(
	`${process.env.GATSBY_BACKOFFICE_BASE_URL}/${PRIZES_ENDPOINT}`
).toString()

export const prizeApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getPrizes: builder.query<
			GetPrizesResponse,
			{
				limit: number
				offset: number
				tenantId: string
				isCabinetPrize?: boolean
				isActive?: boolean
				sort?: `${string}.${'asc' | 'desc'}`
				filters?: Record<string, string>
			}
		>({
			query: ({
				limit,
				offset,
				tenantId,
				isCabinetPrize,
				isActive,
				sort,
				filters,
			}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					queryParams: new Map([
						['tenantId', tenantId],
						[
							'isCabinetPrize',
							typeof isCabinetPrize === 'undefined'
								? ''
								: isCabinetPrize.toString(),
						],
						[
							'isActive',
							typeof isActive === 'undefined' ? '' : isActive.toString(),
						],
					]),
				})

				applyOffsetAndLimit(url.searchParams, offset, limit)
				applySort(url.searchParams, sort)

				const mappedFilters = Object.entries(filters ?? {}).reduce(
					(acc, [key, value]) => {
						acc[`prize.${key}`] = value
						return acc
					},
					{} as Record<string, string>
				)
				applyFilters(url.searchParams, mappedFilters)

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['Prizes'],
		}),
		getPrize: builder.query<PrizeDetail, {tenantId: string; prizeId: string}>({
			query: ({tenantId, prizeId}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZES_ENDPOINT}/${prizeId}`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['Prizes'],
		}),
		updatePrize: builder.mutation<
			void,
			{prizeId: string; tenantId: string; prize: Partial<PrizeDetail>}
		>({
			query: ({prizeId, prize, tenantId}) => {
				return {
					input: `${process.env.GATSBY_BACKOFFICE_BASE_URL}/${PRIZES_ENDPOINT}/${prizeId}?tenantId=${tenantId}`,
					init: {method: 'PUT', body: JSON.stringify(prize)},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
		getCategoriesForPrize: builder.query<
			Partial<PrizeCategory>[],
			{prizeId: string; tenantId: string}
		>({
			query: ({prizeId, tenantId}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZES_ENDPOINT}/${prizeId}/categories`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'GET',
					},
				}
			},
			providesTags: ['Prizes'],
		}),
		updateCategoriesForPrize: builder.mutation<
			void,
			{prizeId: string; tenantId: string; categoryIds: string[]}
		>({
			query: ({prizeId, tenantId, categoryIds}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZES_ENDPOINT}/${prizeId}/categories`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'PUT',
						body: JSON.stringify(categoryIds),
					},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
		getPrizeImageUploadUrls: builder.query<
			{foreground: string; background: string},
			{prizeId: string; tenantId: string}
		>({
			query: ({prizeId, tenantId}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZES_ENDPOINT}/${prizeId}/image-upload`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
		}),
		uploadPrizesCsv: builder.mutation<
			PutPrizesCsvResponse,
			{type: 'prizes' | 'translations'; tenantId: string; csvFile: File}
		>({
			query: ({type, tenantId, csvFile}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					queryParams: new Map([
						['tenantId', tenantId],
						['type', type],
					]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'PUT',
						body: csvFile,
						headers: {
							'Content-Type': 'application/csv',
						},
					},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
		uploadPrizeImage: builder.mutation<
			void,
			{uploadUrl: string; imageFile: File}
		>({
			query: ({uploadUrl, imageFile}) => {
				return {
					input: uploadUrl,
					init: {
						method: 'PUT',
						body: imageFile,
						// Other image types currently result in a 403 HTTP error
						headers: {
							'Content-Type': 'image/png',
							'Content-Length': imageFile.size.toString(),
						},
						credentials: 'omit',
					},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
		updatePrizeTranslation: builder.mutation<
			void,
			{
				tenantId: string
				language: string
				prizeId: string
				translation: Partial<PrizeTranslation>
			}
		>({
			query: ({tenantId, language, prizeId, translation}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZES_ENDPOINT}/${prizeId}/translation`,
					queryParams: new Map([
						['tenantId', tenantId],
						['language', language],
					]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'PUT',
						body: JSON.stringify(translation),
					},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
		getPrizeCategories: builder.query<
			GetPrizeCategoriesResponse,
			{
				offset: number
				limit: number
				tenantId: string
				sort?: {direction: 'asc' | 'desc'; property: string}
			}
		>({
			query: ({offset, limit, tenantId, sort}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZE_CATEGORIES_ENDPOINT}`,
					queryParams: new Map([
						['limit', limit.toString()],
						['offset', offset.toString()],
						['tenantId', tenantId],
						['orderBy', sort ? `${sort.property}.${sort.direction}` : ''],
					]),
				})

				return {
					input: url.toString(),
					init: {method: 'GET'},
				}
			},
			providesTags: ['Prizes'],
		}),
		getPrizeCategory: builder.query<
			PrizeCategory,
			{categoryId: string; tenantId: string}
		>({
			query: ({categoryId, tenantId}) => {
				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZE_CATEGORIES_ENDPOINT}/${categoryId}`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'GET',
					},
				}
			},
			providesTags: ['Prizes'],
		}),
		updatePrizeCategory: builder.mutation<
			void,
			{
				categoryId: string
				tenantId: string
				prizeCategory: Partial<PrizeCategory>
			}
		>({
			query: ({categoryId, tenantId, prizeCategory}) => {
				// Delete these values, to prevent a 400 Bad Request
				delete prizeCategory.id

				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZE_CATEGORIES_ENDPOINT}/${categoryId}`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'PUT',
						body: JSON.stringify(prizeCategory),
					},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
		addPrizeCategory: builder.mutation<
			void,
			{tenantId: string; prizeCategory: Partial<PrizeCategory>}
		>({
			query: ({tenantId, prizeCategory}) => {
				// Delete these values, to prevent a 400 Bad Request
				delete prizeCategory.id

				const url = getUrl(BASE_PRIZE_URL, {
					appendPath: `${PRIZE_CATEGORIES_ENDPOINT}`,
					queryParams: new Map([['tenantId', tenantId]]),
				})

				return {
					input: url.toString(),
					init: {
						method: 'POST',
						body: JSON.stringify(prizeCategory),
					},
				}
			},
			invalidatesTags: ['Prizes'],
		}),
	}),
})

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints.
 */
export const {
	useGetPrizesQuery,
	useLazyGetPrizesQuery,
	useLazyGetPrizeImageUploadUrlsQuery,
	useLazyGetPrizeQuery,
	useUploadPrizesCsvMutation,
	useUpdatePrizeMutation,
	useUploadPrizeImageMutation,
	useUpdatePrizeTranslationMutation,
	useLazyGetCategoriesForPrizeQuery,
	useUpdateCategoriesForPrizeMutation,
	useGetPrizeCategoriesQuery,
	useLazyGetPrizeCategoriesQuery,
	useLazyGetPrizeCategoryQuery,
	useUpdatePrizeCategoryMutation,
	useAddPrizeCategoryMutation,
} = prizeApi
export default prizeApi.reducer
